import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import A4ImageWithExpandable from "../../components/A4ImageWithExpandable/A4ImageWithExpandable.js";
import { fetchImages } from "../../services/imageService";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner.js";
import "./Intentions.css";

const Intentions = () => {
  const [currentImage, setCurrentImage] = useState(null);
  const [pastImages, setPastImages] = useState([]);
  const [urszulaCurrentImage, setUrszulaCurrentImage] = useState(null);
  const [urszulaPastImages, setUrszulaPastImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(null);

  useEffect(() => {
    loadIntentions();
  }, []);

  const loadIntentions = async () => {
    setLoading(true);
    
    // Pobierz intencje tygodniowe
    const images = await fetchImages('intencje');
    
    if (images && images.length > 0) {
      const sortedImages = [...images].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setCurrentImage(sortedImages[0]);
      setCurrentDate(new Date(sortedImages[0].createdAt));
      
      const pastImagesUrls = sortedImages.slice(1).map(img => img.url);
      setPastImages(pastImagesUrls);
    } else {
      setCurrentImage(null);
      setPastImages([]);
      setCurrentDate(null);
    }
    
    // Pobierz intencje dla Mszy Św. Urszuli
    const urszulaImages = await fetchImages('urszula');
    
    if (urszulaImages && urszulaImages.length > 0) {
      const sortedUrszula = [...urszulaImages].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setUrszulaCurrentImage(sortedUrszula[0]);
      
      const urszulaPastImagesUrls = sortedUrszula.slice(1).map(img => img.url);
      setUrszulaPastImages(urszulaPastImagesUrls);
    } else {
      setUrszulaCurrentImage(null);
      setUrszulaPastImages([]);
    }
    
    setLoading(false);
  };

  const formattedDate = currentDate 
    ? currentDate.toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Intencje mszalne – Parafia św. Urszuli Ledóchowskiej w Częstochowie</title>
          <meta name="description" content="Aktualne intencje mszalne w parafii św. Urszuli Ledóchowskiej. Sprawdź intencje na bieżący tydzień i zamów mszę św. w intencji swoich bliskich." />
          <meta property="og:title" content="Intencje mszalne – Parafia św. Urszuli Ledóchowskiej" />
          <meta property="og:description" content="Sprawdź aktualne intencje mszalne w parafii. Zamów mszę św. w intencji swojej rodziny." />
        </Helmet>
        <section className="intentions-loading">
          <LoadingSpinner />
        </section>
      </>
    );
  }

  if (!currentImage && !urszulaCurrentImage) {
    return (
      <>
        <Helmet>
          <title>Intencje mszalne – Parafia św. Urszuli Ledóchowskiej w Częstochowie</title>
          <meta name="description" content="Zamów intencję mszalną online w parafii św. Urszuli Ledóchowskiej w Częstochowie. Sprawdź dostępne terminy i złóż intencję za żywych i zmarłych." />
          <meta property="og:title" content="Intencje mszalne – Parafia św. Urszuli Ledóchowskiej" />
          <meta property="og:description" content="Zamów mszę św. w intencji swoich bliskich. Skontaktuj się z kancelarią parafialną." />
        </Helmet>
        <section className="intentions-empty">
          <p>Brak aktualnych intencji</p>
        </section>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Intencje mszalne – Parafia św. Urszuli Ledóchowskiej w Częstochowie</title>
        <meta name="description" content={`Aktualne intencje mszalne z dnia ${formattedDate} w parafii św. Urszuli Ledóchowskiej w Częstochowie. Sprawdź intencje na bieżący tydzień i zamów mszę św. w intencji swoich bliskich.`} />
        <meta name="keywords" content="intencje mszalne, msze święte, zamów mszę, intencja za żywych, intencja za zmarłych, parafia Częstochowa, św. Urszula Ledóchowska" />
        <meta property="og:title" content={`Intencje mszalne – ${formattedDate}`} />
        <meta property="og:description" content={`Intencje mszalne w parafii św. Urszuli Ledóchowskiej z dnia ${formattedDate}. Sprawdź i zamów mszę św. w intencji swoich bliskich.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://swurszula.pl/intencje" />
        {currentImage && <meta property="og:image" content={currentImage.url} />}
        <meta property="og:image:alt" content="Intencje mszalne - ogłoszenia parafialne" />
        <meta property="og:locale" content="pl_PL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Intencje mszalne – ${formattedDate}`} />
        <meta name="twitter:description" content={`Aktualne intencje mszalne z dnia ${formattedDate} w parafii św. Urszuli Ledóchowskiej.`} />
        {currentImage && <meta name="twitter:image" content={currentImage.url} />}
      </Helmet>

      <section className="intentions-section">
        {/* Intencje tygodniowe */}
        {currentImage && (
          <div className="weekly-intentions">
            <A4ImageWithExpandable
              mainImage={currentImage.url}
              expandedImage={pastImages}
              linkLabel="Intencje - poprzednie tygodnie"
              currentImageDate={new Date(currentImage.createdAt).toLocaleDateString('pl-PL')}
            />
          </div>
        )}

        {/* Intencja Mszy Św. Urszuli - ten sam komponent */}
        {urszulaCurrentImage && (
          <div className="urszula-intentions">
            <A4ImageWithExpandable
              mainImage={urszulaCurrentImage.url}
              expandedImage={urszulaPastImages}
              linkLabel="Intencje św. Urszuli - poprzednie"
              currentImageDate={new Date(urszulaCurrentImage.createdAt).toLocaleDateString('pl-PL')}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default Intentions;